/**
 * Everything Tea Widgets SDK
 * Handles Loyalty, Wishlist, Birthday, and Referral functionality
 * Version: 1.0.0
 */

(function(window) {
  'use strict';

  // ===========================================
  // CONFIGURATION
  // ===========================================

  const CONFIG = {
    apiBase: 'https://europe-west1-everything-tea.cloudfunctions.net',
    storeDomain: 'everythingtea.co.za',
    currency: 'R',
    debug: false
  };

  // ===========================================
  // UTILITY FUNCTIONS
  // ===========================================

  const Utils = {
    log(...args) {
      if (CONFIG.debug) console.log('[ET]', ...args);
    },

    error(...args) {
      console.error('[ET Error]', ...args);
    },

    async api(endpoint, options = {}) {
      const url = `${CONFIG.apiBase}${endpoint}`;

      try {
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          },
          ...options
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        Utils.error(`API call failed: ${endpoint}`, error);
        throw error;
      }
    },

    getCustomerId() {
      // Try to get from Shopify customer object
      if (window.__st && window.__st.cid) {
        return window.__st.cid.toString();
      }
      // Try meta tag
      const meta = document.querySelector('meta[name="customer-id"]');
      if (meta && meta.content) return meta.content;
      // Try data attribute
      const el = document.querySelector('[data-customer-id]');
      if (el) return el.dataset.customerId;
      return null;
    },

    getCustomerEmail() {
      const meta = document.querySelector('meta[name="customer-email"]');
      if (meta && meta.content) return meta.content;
      const el = document.querySelector('[data-customer-email]');
      if (el) return el.dataset.customerEmail;
      return null;
    },

    getSessionId() {
      let sessionId = localStorage.getItem('et_session_id');
      if (!sessionId) {
        sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) +
                    Date.now().toString(36);
        localStorage.setItem('et_session_id', sessionId);
      }
      return sessionId;
    },

    formatNumber(num) {
      return num.toLocaleString('en-ZA');
    },

    formatCurrency(amount) {
      return `${CONFIG.currency}${amount.toFixed(2)}`;
    },

    createElement(tag, className, innerHTML = '') {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (innerHTML) el.innerHTML = innerHTML;
      return el;
    },

    showNotification(message, type = 'success') {
      const notification = Utils.createElement('div', `et-notification et-notification--${type}`);
      notification.innerHTML = `
        <span class="et-notification__message">${message}</span>
        <button class="et-notification__close" onclick="this.parentElement.remove()">×</button>
      `;
      document.body.appendChild(notification);

      setTimeout(() => notification.classList.add('et-notification--visible'), 10);
      setTimeout(() => {
        notification.classList.remove('et-notification--visible');
        setTimeout(() => notification.remove(), 300);
      }, 4000);
    },

    showModal(content, options = {}) {
      const overlay = Utils.createElement('div', 'et-modal-overlay');
      const modal = Utils.createElement('div', 'et-modal');

      modal.innerHTML = `
        <button class="et-modal__close" aria-label="Close">×</button>
        <div class="et-modal__content">${content}</div>
      `;

      overlay.appendChild(modal);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';

      const close = () => {
        overlay.classList.remove('et-modal-overlay--visible');
        setTimeout(() => {
          overlay.remove();
          document.body.style.overflow = '';
        }, 300);
      };

      overlay.querySelector('.et-modal__close').onclick = close;
      overlay.onclick = (e) => {
        if (e.target === overlay) close();
      };

      setTimeout(() => overlay.classList.add('et-modal-overlay--visible'), 10);

      return { close, modal };
    }
  };

  // ===========================================
  // LOYALTY WIDGET
  // ===========================================

  class LoyaltyWidget {
    constructor() {
      this.customerId = Utils.getCustomerId();
      this.data = null;
      this.containers = [];

      Utils.log('LoyaltyWidget initialized', { customerId: this.customerId });
    }

    async init() {
      if (!this.customerId) {
        Utils.log('No customer ID, loyalty widget not available');
        return;
      }

      // Find all loyalty widget containers
      this.containers = document.querySelectorAll('[data-et-loyalty-widget]');

      if (this.containers.length === 0) {
        Utils.log('No loyalty widget containers found');
        return;
      }

      await this.loadData();
      this.render();
    }

    async loadData() {
      try {
        this.data = await Utils.api(`/getLoyaltyStatus?customerId=${this.customerId}`);
        Utils.log('Loyalty data loaded', this.data);
      } catch (error) {
        Utils.error('Failed to load loyalty data', error);
        this.data = null;
      }
    }

    render() {
      if (!this.data) {
        this.containers.forEach(container => {
          container.innerHTML = `
            <div class="et-loyalty et-loyalty--error">
              <p>Unable to load your rewards. Please try again later.</p>
            </div>
          `;
        });
        return;
      }

      const html = this.buildWidget();
      this.containers.forEach(container => {
        container.innerHTML = html;
        this.attachEventListeners(container);
      });
    }

    buildWidget() {
      const { pointsBalance, lifetimePoints, tier, referralCode, redemptions } = this.data;
      const progress = this.calculateProgress();

      return `
        <div class="et-loyalty">
          <!-- Header with tier and points -->
          <div class="et-loyalty__header">
            <div class="et-loyalty__tier">
              <span class="et-loyalty__tier-badge et-loyalty__tier-badge--${tier}"></span>
              <span class="et-loyalty__tier-name">${this.formatTierName(tier)} Member</span>
            </div>
            <div class="et-loyalty__points">
              <span class="et-loyalty__points-value">${Utils.formatNumber(pointsBalance)}</span>
              <span class="et-loyalty__points-label">Points Available</span>
            </div>
          </div>

          <!-- Progress to next tier -->
          <div class="et-loyalty__progress">
            <div class="et-loyalty__progress-bar">
              <div class="et-loyalty__progress-fill" style="width: ${progress.percent}%"></div>
            </div>
            <p class="et-loyalty__progress-text">${progress.text}</p>
          </div>

          <!-- Tier benefits -->
          <div class="et-loyalty__benefits">
            <h4>Your ${this.formatTierName(tier)} Benefits</h4>
            <ul>
              ${this.getTierBenefits(tier).map(b => `<li>${b}</li>`).join('')}
            </ul>
          </div>

          <!-- Redemption options -->
          <div class="et-loyalty__redemptions">
            <h3>Redeem Your Points</h3>
            <div class="et-loyalty__redemption-grid">
              ${redemptions.map((r, i) => this.buildRedemptionCard(r, i, pointsBalance)).join('')}
            </div>
          </div>

          <!-- Referral section -->
          <div class="et-loyalty__referral">
            <h3>Invite Friends, Earn Points</h3>
            <p>Share your unique code and you'll both earn bonus points when they make their first purchase!</p>
            <div class="et-loyalty__referral-code">
              <input type="text" readonly value="${referralCode}" id="et-referral-code">
              <button class="et-btn et-btn--secondary" data-action="copy-referral">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy
              </button>
            </div>
            <div class="et-loyalty__referral-share">
              <button class="et-btn et-btn--icon" data-action="share-whatsapp" title="Share on WhatsApp">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </button>
              <button class="et-btn et-btn--icon" data-action="share-email" title="Share via Email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </button>
            </div>
          </div>

          <!-- Points history link -->
          <div class="et-loyalty__footer">
            <a href="/account" class="et-link">View Points History →</a>
          </div>
        </div>
      `;
    }

    buildRedemptionCard(redemption, index, balance) {
      const canRedeem = balance >= redemption.points;

      return `
        <div class="et-loyalty__redemption-card ${canRedeem ? '' : 'et-loyalty__redemption-card--disabled'}">
          <div class="et-loyalty__redemption-value">${redemption.label}</div>
          <div class="et-loyalty__redemption-points">${Utils.formatNumber(redemption.points)} points</div>
          <button
            class="et-btn ${canRedeem ? 'et-btn--primary' : 'et-btn--disabled'}"
            ${canRedeem ? `data-action="redeem" data-redemption-id="${index}"` : 'disabled'}
          >
            ${canRedeem ? 'Redeem' : `Need ${Utils.formatNumber(redemption.points - balance)} more`}
          </button>
        </div>
      `;
    }

    calculateProgress() {
      const tiers = {
        bronze: { min: 0, max: 500, next: 'Silver' },
        silver: { min: 500, max: 2000, next: 'Gold' },
        gold: { min: 2000, max: 5000, next: 'Platinum' },
        platinum: { min: 5000, max: Infinity, next: null }
      };

      const currentTier = tiers[this.data.tier];

      if (!currentTier.next) {
        return { percent: 100, text: "You've reached our highest tier!" };
      }

      const progress = ((this.data.lifetimePoints - currentTier.min) / (currentTier.max - currentTier.min)) * 100;
      const pointsNeeded = currentTier.max - this.data.lifetimePoints;

      return {
        percent: Math.min(Math.max(progress, 0), 100),
        text: `${Utils.formatNumber(pointsNeeded)} points to ${currentTier.next}`
      };
    }

    formatTierName(tier) {
      return tier.charAt(0).toUpperCase() + tier.slice(1);
    }

    getTierBenefits(tier) {
      const benefits = {
        bronze: [
          'Earn 1 point per R10 spent',
          'Birthday bonus points',
          'Exclusive member offers'
        ],
        silver: [
          'Earn 1.25x points on all purchases',
          'Early access to sales',
          'Free shipping on orders over R500'
        ],
        gold: [
          'Earn 1.5x points on all purchases',
          'Priority customer support',
          'Exclusive Gold member discounts',
          'Free shipping on all orders'
        ],
        platinum: [
          'Earn 2x points on all purchases',
          'VIP access to new products',
          'Complimentary gift wrapping',
          'Annual surprise gift',
          'Free express shipping'
        ]
      };

      return benefits[tier] || benefits.bronze;
    }

    attachEventListeners(container) {
      // Redeem button
      container.querySelectorAll('[data-action="redeem"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const redemptionId = parseInt(btn.dataset.redemptionId);
          this.handleRedeem(redemptionId);
        });
      });

      // Copy referral code
      container.querySelectorAll('[data-action="copy-referral"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const input = container.querySelector('#et-referral-code');
          input.select();
          navigator.clipboard.writeText(input.value).then(() => {
            Utils.showNotification('Referral code copied!');
          }).catch(() => {
            document.execCommand('copy');
            Utils.showNotification('Referral code copied!');
          });
        });
      });

      // Share WhatsApp
      container.querySelectorAll('[data-action="share-whatsapp"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const code = this.data.referralCode;
          const text = `Shop at Everything Tea and use my referral code ${code} to get bonus points! https://${CONFIG.storeDomain}?ref=${code}`;
          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        });
      });

      // Share Email
      container.querySelectorAll('[data-action="share-email"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const code = this.data.referralCode;
          const subject = 'Join me at Everything Tea!';
          const body = `I've been enjoying amazing teas from Everything Tea and thought you might like them too!\n\nUse my referral code ${code} when you sign up and we'll both earn bonus points.\n\nShop now: https://${CONFIG.storeDomain}?ref=${code}`;
          window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        });
      });
    }

    async handleRedeem(redemptionId) {
      const redemption = this.data.redemptions[redemptionId];

      const confirmed = confirm(
        `Redeem ${Utils.formatNumber(redemption.points)} points for ${redemption.label}?`
      );

      if (!confirmed) return;

      try {
        const result = await Utils.api('/redeemPoints', {
          method: 'POST',
          body: JSON.stringify({
            customerId: this.customerId,
            redemptionId
          })
        });

        if (result.success) {
          this.showRedemptionSuccess(result);
          await this.loadData();
          this.render();
        } else {
          Utils.showNotification(result.error || 'Redemption failed', 'error');
        }
      } catch (error) {
        Utils.showNotification('Something went wrong. Please try again.', 'error');
      }
    }

    showRedemptionSuccess(result) {
      Utils.showModal(`
        <div class="et-redemption-success">
          <div class="et-redemption-success__icon">🎉</div>
          <h2>Congratulations!</h2>
          <p>Your reward has been redeemed successfully.</p>

          <div class="et-redemption-success__code">
            <label>Your Discount Code</label>
            <div class="et-redemption-success__code-box">
              <span id="et-discount-code">${result.discountCode}</span>
              <button class="et-btn et-btn--small" onclick="
                navigator.clipboard.writeText('${result.discountCode}');
                this.textContent = 'Copied!';
                setTimeout(() => this.textContent = 'Copy', 2000);
              ">Copy</button>
            </div>
          </div>

          <p class="et-redemption-success__expiry">
            Valid until ${new Date(result.expiresAt).toLocaleDateString('en-ZA', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>

          <p class="et-redemption-success__balance">
            New points balance: <strong>${Utils.formatNumber(result.newBalance)}</strong>
          </p>

          <a href="/collections/all" class="et-btn et-btn--primary et-btn--large">
            Start Shopping
          </a>
        </div>
      `);
    }
  }

  // ===========================================
  // WISHLIST MANAGER
  // ===========================================

  class WishlistManager {
    constructor() {
      this.customerId = Utils.getCustomerId();
      this.sessionId = Utils.getSessionId();
      this.email = Utils.getCustomerEmail();
      this.items = [];
      this.wishlistId = null;

      Utils.log('WishlistManager initialized');
    }

    async init() {
      await this.loadWishlist();
      this.initButtons();
      this.updateHeaderIcon();
      this.initWishlistPage();
    }

    async loadWishlist() {
      try {
        const params = new URLSearchParams();
        if (this.customerId) {
          params.set('customerId', this.customerId);
        } else {
          params.set('sessionId', this.sessionId);
        }

        const data = await Utils.api(`/getWishlist?${params}`);
        this.items = data.items || [];
        this.wishlistId = data.id;
        Utils.log('Wishlist loaded', { items: this.items.length });
      } catch (error) {
        Utils.log('No existing wishlist');
        this.items = [];
      }
    }

    initButtons() {
      // Find all wishlist buttons
      document.querySelectorAll('[data-et-wishlist-button]').forEach(btn => {
        const productId = btn.dataset.productId;
        const variantId = btn.dataset.variantId;

        // Set initial state
        this.updateButtonState(btn, productId, variantId);

        // Add click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleItem(btn, productId, variantId);
        });
      });
    }

    updateButtonState(btn, productId, variantId) {
      const isInWishlist = this.isInWishlist(productId, variantId);

      btn.classList.toggle('et-wishlist-btn--active', isInWishlist);
      btn.setAttribute('aria-pressed', isInWishlist);

      const text = btn.querySelector('.et-wishlist-btn__text');
      if (text) {
        text.textContent = isInWishlist ? 'In Wishlist' : 'Add to Wishlist';
      }
    }

    isInWishlist(productId, variantId) {
      return this.items.some(item =>
        item.productId == productId && item.variantId == variantId
      );
    }

    async toggleItem(btn, productId, variantId) {
      btn.disabled = true;
      btn.classList.add('et-wishlist-btn--loading');

      try {
        if (this.isInWishlist(productId, variantId)) {
          await this.removeItem(productId, variantId);
          Utils.showNotification('Removed from wishlist');
        } else {
          await this.addItem(productId, variantId);
          Utils.showNotification('Added to wishlist!');
        }

        this.updateButtonState(btn, productId, variantId);
        this.updateHeaderIcon();

      } catch (error) {
        Utils.showNotification('Could not update wishlist', 'error');
      } finally {
        btn.disabled = false;
        btn.classList.remove('et-wishlist-btn--loading');
      }
    }

    async addItem(productId, variantId) {
      const result = await Utils.api('/addToWishlist', {
        method: 'POST',
        body: JSON.stringify({
          customerId: this.customerId,
          sessionId: this.sessionId,
          email: this.email,
          productId: productId.toString(),
          variantId: variantId.toString()
        })
      });

      if (result.success) {
        this.items.push({ productId, variantId });
        this.wishlistId = result.wishlistId;
      }

      return result;
    }

    async removeItem(productId, variantId) {
      const result = await Utils.api('/removeFromWishlist', {
        method: 'POST',
        body: JSON.stringify({
          customerId: this.customerId,
          sessionId: this.sessionId,
          productId: productId.toString(),
          variantId: variantId.toString()
        })
      });

      if (result.success) {
        this.items = this.items.filter(item =>
          !(item.productId == productId && item.variantId == variantId)
        );
      }

      return result;
    }

    updateHeaderIcon() {
      const countElements = document.querySelectorAll('[data-et-wishlist-count]');
      countElements.forEach(el => {
        el.textContent = this.items.length;
        el.style.display = this.items.length > 0 ? '' : 'none';
      });
    }

    initWishlistPage() {
      const container = document.querySelector('[data-et-wishlist-page]');
      if (!container) return;

      this.renderWishlistPage(container);
    }

    async renderWishlistPage(container) {
      if (this.items.length === 0) {
        container.innerHTML = `
          <div class="et-wishlist-empty">
            <div class="et-wishlist-empty__icon">💝</div>
            <h2>Your wishlist is empty</h2>
            <p>Save your favorite teas and products here!</p>
            <a href="/collections/all" class="et-btn et-btn--primary">Start Browsing</a>
          </div>
        `;
        return;
      }

      // Fetch full product details
      try {
        const params = new URLSearchParams();
        if (this.customerId) {
          params.set('customerId', this.customerId);
        } else {
          params.set('sessionId', this.sessionId);
        }

        const data = await Utils.api(`/getWishlist?${params}`);
        this.renderWishlistItems(container, data.items);

      } catch (error) {
        container.innerHTML = '<p>Error loading wishlist. Please try again.</p>';
      }
    }

    renderWishlistItems(container, items) {
      container.innerHTML = `
        <div class="et-wishlist-page">
          <div class="et-wishlist-page__header">
            <h1>My Wishlist</h1>
            <span class="et-wishlist-page__count">${items.length} ${items.length === 1 ? 'item' : 'items'}</span>
          </div>

          <div class="et-wishlist-page__actions">
            <button class="et-btn et-btn--secondary" data-action="share-wishlist">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              Share Wishlist
            </button>
          </div>

          <div class="et-wishlist-page__grid">
            ${items.map(item => this.renderWishlistItem(item)).join('')}
          </div>
        </div>
      `;

      // Attach event listeners
      container.querySelectorAll('[data-action="remove-item"]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const productId = btn.dataset.productId;
          const variantId = btn.dataset.variantId;

          await this.removeItem(productId, variantId);
          this.renderWishlistPage(container);
          this.updateHeaderIcon();
          Utils.showNotification('Removed from wishlist');
        });
      });

      container.querySelectorAll('[data-action="share-wishlist"]').forEach(btn => {
        btn.addEventListener('click', () => this.showShareModal());
      });
    }

    renderWishlistItem(item) {
      const priceChanged = item.currentPrice && item.currentPrice < item.priceAtAdd;

      return `
        <div class="et-wishlist-item">
          <a href="${item.productUrl}" class="et-wishlist-item__image">
            <img src="${item.imageUrl || '/no-image.jpg'}" alt="${item.productTitle}" loading="lazy">
            ${priceChanged ? '<span class="et-wishlist-item__badge">Price Drop!</span>' : ''}
          </a>

          <div class="et-wishlist-item__details">
            <h3 class="et-wishlist-item__title">
              <a href="${item.productUrl}">${item.productTitle}</a>
            </h3>
            ${item.variantTitle ? `<p class="et-wishlist-item__variant">${item.variantTitle}</p>` : ''}

            <div class="et-wishlist-item__price">
              <span class="et-wishlist-item__current-price">${Utils.formatCurrency(item.currentPrice || item.priceAtAdd)}</span>
              ${priceChanged ? `<span class="et-wishlist-item__original-price">${Utils.formatCurrency(item.priceAtAdd)}</span>` : ''}
            </div>

            <div class="et-wishlist-item__actions">
              <a href="${item.productUrl}" class="et-btn et-btn--primary et-btn--small">View Product</a>
              <button class="et-btn et-btn--icon" data-action="remove-item" data-product-id="${item.productId}" data-variant-id="${item.variantId}" title="Remove">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    showShareModal() {
      const { modal, close } = Utils.showModal(`
        <div class="et-share-wishlist">
          <h2>Share Your Wishlist</h2>
          <p>Send your wishlist to friends and family!</p>

          <form id="et-share-form">
            <div class="et-form-group">
              <label for="et-share-name">Your Name</label>
              <input type="text" id="et-share-name" required placeholder="Enter your name">
            </div>

            <div class="et-form-group">
              <label for="et-share-emails">Email Addresses</label>
              <textarea id="et-share-emails" required placeholder="Enter email addresses, separated by commas"></textarea>
            </div>

            <div class="et-form-group">
              <label for="et-share-message">Personal Message (optional)</label>
              <textarea id="et-share-message" placeholder="Add a personal note..."></textarea>
            </div>

            <button type="submit" class="et-btn et-btn--primary et-btn--large">Send Wishlist</button>
          </form>
        </div>
      `);

      modal.querySelector('#et-share-form').addEventListener('submit', async (e) => {
        e.preventDefault();

        const name = modal.querySelector('#et-share-name').value;
        const emails = modal.querySelector('#et-share-emails').value
          .split(',')
          .map(e => e.trim())
          .filter(e => e);
        const message = modal.querySelector('#et-share-message').value;

        try {
          await Utils.api('/shareWishlist', {
            method: 'POST',
            body: JSON.stringify({
              wishlistId: this.wishlistId,
              recipientEmails: emails,
              senderName: name,
              message
            })
          });

          close();
          Utils.showNotification('Wishlist shared successfully!');
        } catch (error) {
          Utils.showNotification('Failed to share wishlist', 'error');
        }
      });
    }
  }

  // ===========================================
  // BIRTHDAY COLLECTOR
  // ===========================================

  class BirthdayCollector {
    constructor() {
      this.customerId = Utils.getCustomerId();
      this.email = Utils.getCustomerEmail();
      this.hasShownPopup = sessionStorage.getItem('et_birthday_popup_shown');

      Utils.log('BirthdayCollector initialized');
    }

    async init() {
      // Check if birthday already collected
      if (!this.customerId) return;

      const hasBirthday = await this.checkHasBirthday();

      if (!hasBirthday && !this.hasShownPopup) {
        // Show popup after 30 seconds on site
        setTimeout(() => this.showPopup(), 30000);
      }

      // Initialize any embedded birthday forms
      this.initForms();
    }

    async checkHasBirthday() {
      try {
        const data = await Utils.api(`/getLoyaltyStatus?customerId=${this.customerId}`);
        return !!data.hasBirthday;
      } catch {
        return false;
      }
    }

    initForms() {
      document.querySelectorAll('[data-et-birthday-form]').forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e, form));
      });
    }

    showPopup() {
      if (this.hasShownPopup) return;

      sessionStorage.setItem('et_birthday_popup_shown', 'true');
      this.hasShownPopup = true;

      const { modal, close } = Utils.showModal(`
        <div class="et-birthday-popup">
          <div class="et-birthday-popup__icon">🎂</div>
          <h2>When's Your Birthday?</h2>
          <p>Share your birthday and we'll send you a special surprise with bonus points and an exclusive discount!</p>

          <form id="et-birthday-popup-form" class="et-birthday-form">
            <div class="et-form-row">
              <div class="et-form-group">
                <label for="et-birthday-day">Day</label>
                <select id="et-birthday-day" required>
                  <option value="">Day</option>
                  ${Array.from({length: 31}, (_, i) => `<option value="${i+1}">${i+1}</option>`).join('')}
                </select>
              </div>

              <div class="et-form-group">
                <label for="et-birthday-month">Month</label>
                <select id="et-birthday-month" required>
                  <option value="">Month</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
            </div>

            <p class="et-birthday-form__note">We only need the day and month - no year required!</p>

            <button type="submit" class="et-btn et-btn--primary et-btn--large">
              Unlock My Birthday Reward
            </button>
          </form>

          <button class="et-link" onclick="this.closest('.et-modal-overlay').querySelector('.et-modal__close').click()">
            Maybe Later
          </button>
        </div>
      `);

      modal.querySelector('#et-birthday-popup-form').addEventListener('submit', async (e) => {
        e.preventDefault();

        const day = modal.querySelector('#et-birthday-day').value;
        const month = modal.querySelector('#et-birthday-month').value;

        const success = await this.saveBirthday(day, month);

        if (success) {
          close();
          Utils.showNotification('Birthday saved! Look out for a special surprise!');
        }
      });
    }

    async handleSubmit(e, form) {
      e.preventDefault();

      const day = form.querySelector('[name="birthday_day"]').value;
      const month = form.querySelector('[name="birthday_month"]').value;

      const success = await this.saveBirthday(day, month);

      if (success) {
        form.innerHTML = `
          <div class="et-birthday-form__success">
            <span>🎉</span>
            <p>Thanks! We'll make your birthday special!</p>
          </div>
        `;
      }
    }

    async saveBirthday(day, month) {
      try {
        // Create a date (using 2000 as placeholder year)
        const birthday = new Date(2000, parseInt(month) - 1, parseInt(day));

        await Utils.api('/collectBirthday', {
          method: 'POST',
          body: JSON.stringify({
            customerId: this.customerId,
            email: this.email,
            birthday: birthday.toISOString()
          })
        });

        return true;
      } catch (error) {
        Utils.showNotification('Could not save birthday. Please try again.', 'error');
        return false;
      }
    }
  }

  // ===========================================
  // FLOATING LOYALTY LAUNCHER
  // ===========================================

  class LoyaltyLauncher {
    constructor() {
      this.customerId = Utils.getCustomerId();
      this.isOpen = false;
      this.data = null;
    }

    async init() {
      const launcher = document.querySelector('[data-et-loyalty-launcher]');
      if (!launcher || !this.customerId) return;

      // Load initial data
      try {
        this.data = await Utils.api(`/getLoyaltyStatus?customerId=${this.customerId}`);
        this.render(launcher);
      } catch (error) {
        Utils.log('Could not load loyalty data for launcher');
      }
    }

    render(container) {
      container.innerHTML = `
        <button class="et-launcher" aria-label="Open rewards">
          <span class="et-launcher__icon">🎁</span>
          <span class="et-launcher__badge">${Utils.formatNumber(this.data.pointsBalance)}</span>
        </button>

        <div class="et-launcher__panel">
          <div class="et-launcher__header">
            <h3>Your Rewards</h3>
            <button class="et-launcher__close" aria-label="Close">×</button>
          </div>

          <div class="et-launcher__content">
            <div class="et-launcher__points">
              <span class="et-launcher__points-value">${Utils.formatNumber(this.data.pointsBalance)}</span>
              <span class="et-launcher__points-label">points available</span>
            </div>

            <div class="et-launcher__tier">
              <span class="et-launcher__tier-badge et-launcher__tier-badge--${this.data.tier}"></span>
              <span>${this.data.tier.charAt(0).toUpperCase() + this.data.tier.slice(1)} Member</span>
            </div>

            <div class="et-launcher__actions">
              <a href="/pages/rewards" class="et-btn et-btn--primary">View All Rewards</a>
              <a href="/pages/rewards#redeem" class="et-btn et-btn--secondary">Redeem Points</a>
            </div>

            <div class="et-launcher__ways">
              <h4>Ways to Earn</h4>
              <ul>
                <li>🛒 1 point per R10 spent</li>
                <li>👥 500 points per referral</li>
                <li>🎂 200 birthday bonus points</li>
              </ul>
            </div>
          </div>
        </div>
      `;

      // Toggle panel
      const btn = container.querySelector('.et-launcher');
      const panel = container.querySelector('.et-launcher__panel');
      const closeBtn = container.querySelector('.et-launcher__close');

      btn.addEventListener('click', () => {
        this.isOpen = !this.isOpen;
        panel.classList.toggle('et-launcher__panel--open', this.isOpen);
      });

      closeBtn.addEventListener('click', () => {
        this.isOpen = false;
        panel.classList.remove('et-launcher__panel--open');
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!container.contains(e.target) && this.isOpen) {
          this.isOpen = false;
          panel.classList.remove('et-launcher__panel--open');
        }
      });
    }
  }

  // ===========================================
  // REFERRAL HANDLER
  // ===========================================

  class ReferralHandler {
    constructor() {
      this.referralCode = this.getRefFromUrl();
    }

    init() {
      if (this.referralCode) {
        // Store in localStorage for later use
        localStorage.setItem('et_referral_code', this.referralCode);
        Utils.log('Referral code stored:', this.referralCode);

        // Show a subtle notification
        this.showReferralBanner();
      }
    }

    getRefFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return params.get('ref');
    }

    showReferralBanner() {
      const banner = Utils.createElement('div', 'et-referral-banner');
      banner.innerHTML = `
        <p>You've been referred! Complete a purchase to claim your bonus points.</p>
        <button onclick="this.parentElement.remove()">×</button>
      `;
      document.body.prepend(banner);

      setTimeout(() => banner.classList.add('et-referral-banner--visible'), 100);
    }

    async applyReferral(newCustomerId, email) {
      const storedCode = localStorage.getItem('et_referral_code');
      if (!storedCode) return;

      try {
        await Utils.api('/processReferral', {
          method: 'POST',
          body: JSON.stringify({
            referralCode: storedCode,
            newCustomerId,
            newCustomerEmail: email
          })
        });

        localStorage.removeItem('et_referral_code');
        Utils.showNotification('Referral bonus applied!');
      } catch (error) {
        Utils.log('Referral application failed', error);
      }
    }
  }

  // ===========================================
  // MAIN SDK INITIALIZATION
  // ===========================================

  class EverythingTeaSDK {
    constructor() {
      this.loyalty = new LoyaltyWidget();
      this.wishlist = new WishlistManager();
      this.birthday = new BirthdayCollector();
      this.launcher = new LoyaltyLauncher();
      this.referral = new ReferralHandler();

      Utils.log('Everything Tea SDK loaded');
    }

    async init() {
      try {
        // Initialize all modules
        await Promise.all([
          this.loyalty.init(),
          this.wishlist.init(),
          this.birthday.init(),
          this.launcher.init()
        ]);

        this.referral.init();

        Utils.log('Everything Tea SDK initialized');
      } catch (error) {
        Utils.error('SDK initialization error:', error);
      }
    }

    // Public API
    getWishlistCount() {
      return this.wishlist.items.length;
    }

    getLoyaltyPoints() {
      return this.loyalty.data?.pointsBalance || 0;
    }

    async addToWishlist(productId, variantId) {
      return this.wishlist.addItem(productId, variantId);
    }

    async removeFromWishlist(productId, variantId) {
      return this.wishlist.removeItem(productId, variantId);
    }

    isInWishlist(productId, variantId) {
      return this.wishlist.isInWishlist(productId, variantId);
    }

    showBirthdayPopup() {
      this.birthday.showPopup();
    }
  }

  // ===========================================
  // AUTO-INITIALIZE
  // ===========================================

  // Create global instance
  window.EverythingTea = new EverythingTeaSDK();

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.EverythingTea.init());
  } else {
    window.EverythingTea.init();
  }

})(window);
